import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, Check, Car, Trash2 } from 'lucide-react';
import { useNotificationStore } from '@/store';
import type { AppNotification } from '@/types';

function useTimeAgo() {
  const { t } = useTranslation();
  return (iso: string): string => {
    const diffMs = Date.now() - new Date(iso).getTime();
    const min = Math.floor(diffMs / 60_000);
    if (min < 1) return t('notifications.justNow', 'Just now');
    if (min < 60) return t('notifications.minutesAgo', '{{count}}m ago', { count: min });
    const hr = Math.floor(min / 60);
    if (hr < 24) return t('notifications.hoursAgo', '{{count}}h ago', { count: hr });
    const days = Math.floor(hr / 24);
    return t('notifications.daysAgo', '{{count}}d ago', { count: days });
  };
}

export function NotificationsBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const timeAgo = useTimeAgo();
  const [isOpen, setIsOpen] = useState(false);
  const {
    notifications,
    unreadCount,
    isLoading,
    fetchNotifications,
    markRead,
    markAllRead,
    remove,
    startPolling,
    stopPolling,
  } = useNotificationStore();

  // Poll the unread badge while the bell is mounted (authenticated session).
  useEffect(() => {
    startPolling();
    return () => stopPolling();
  }, [startPolling, stopPolling]);

  useEffect(() => {
    if (isOpen) void fetchNotifications();
  }, [isOpen, fetchNotifications]);

  const handleOpen = (notification: AppNotification) => {
    void markRead(notification.id);
    setIsOpen(false);
    const carId = notification.carId || notification.data?.carId;
    if (carId) navigate(`/cars/${carId}`);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen((v) => !v)}
        className="p-2 text-gray-600 hover:text-primary-600 transition-colors relative"
        title={t('notifications.title', 'Notifications')}
        aria-label={t('notifications.title', 'Notifications')}
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -end-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 px-1 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Outside-click backdrop */}
            <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute end-0 mt-2 w-80 sm:w-96 max-h-[70vh] overflow-hidden bg-white/95 backdrop-blur-md rounded-xl shadow-lg border border-white/50 z-50 flex flex-col"
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-gray-900">
                  {t('notifications.title', 'Notifications')}
                </h3>
                {unreadCount > 0 && (
                  <button
                    onClick={() => void markAllRead()}
                    className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
                  >
                    <Check className="w-3.5 h-3.5" />
                    {t('notifications.markAllRead', 'Mark all read')}
                  </button>
                )}
              </div>

              <div className="overflow-y-auto">
                {isLoading && notifications.length === 0 ? (
                  <div className="px-4 py-8 text-center text-sm text-gray-400">
                    {t('common.loading', 'Loading…')}
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="px-4 py-10 text-center">
                    <Bell className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                    <p className="text-sm text-gray-500">{t('notifications.empty', 'No notifications yet')}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {t('notifications.emptyHint', 'Turn on alerts for a saved search to get notified about new cars.')}
                    </p>
                  </div>
                ) : (
                  notifications.map((n) => (
                    <div
                      key={n.id}
                      className={`group flex items-start gap-3 px-4 py-3 border-b border-gray-50 hover:bg-gray-50 cursor-pointer ${
                        n.isRead ? '' : 'bg-blue-50/40'
                      }`}
                      onClick={() => handleOpen(n)}
                    >
                      <div className="mt-0.5 flex-shrink-0 w-9 h-9 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center">
                        <Car className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm truncate ${n.isRead ? 'text-gray-700' : 'text-gray-900 font-semibold'}`}>
                          {n.title}
                        </p>
                        {n.body && <p className="text-xs text-gray-500 truncate">{n.body}</p>}
                        <p className="text-[11px] text-gray-400 mt-0.5">{timeAgo(n.createdAt)}</p>
                      </div>
                      {!n.isRead && <span className="mt-1.5 flex-shrink-0 w-2 h-2 rounded-full bg-blue-500" />}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          void remove(n.id);
                        }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-gray-400 hover:text-red-500"
                        aria-label={t('notifications.dismiss', 'Dismiss')}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
